import { useEffect, useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

type Size = 'sm' | 'md' | 'lg' | 'fullscreen'

type Props = {
  open: boolean
  onClose: () => void
  title?: string
  description?: string
  size?: Size
  /** Footer renderizado en una banda fija inferior (no parte del scroll). */
  footer?: ReactNode
  /** Si false, ignora Escape y click en overlay (modal de confirmación destructiva). */
  dismissible?: boolean
  /** Etiqueta del aria-label cuando no hay title. */
  ariaLabel?: string
  children: ReactNode
}

/**
 * Modal base de la plataforma — implementa REQ-UI-MOD-01 / §15.1 SRS:
 *   - Cierre por Escape (a menos que dismissible=false).
 *   - Cierre por click en overlay (clicks DENTRO del modal no cierran).
 *   - Botón explícito de cierre (X) en esquina superior derecha.
 *   - Scroll interno cuando el contenido excede la altura de la pantalla.
 *   - Overlay semitransparente sobre el contenido de fondo.
 *   - Bloquea scroll del body mientras está abierto.
 *   - Devuelve el foco al elemento previo al cerrarse.
 *   - Focus trap básico: Tab cycla dentro del modal.
 *
 * Implementado con <dialog> nativo no — usamos portal + div con role="dialog"
 * para tener mayor control del look y soporte iOS Safari (que tuvo bugs con <dialog>).
 */
export function Modal({
  open,
  onClose,
  title,
  description,
  size = 'md',
  footer,
  dismissible = true,
  ariaLabel,
  children,
}: Props) {
  const previousFocusRef = useRef<HTMLElement | null>(null)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const mouseDownOnOverlay = useRef(false)

  // Bloquear scroll del body y guardar foco previo.
  useEffect(() => {
    if (!open) return
    previousFocusRef.current = (document.activeElement as HTMLElement | null) ?? null
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    // Devolver el foco al elemento que abrió el modal al cerrarse.
    return () => {
      document.body.style.overflow = previousOverflow
      previousFocusRef.current?.focus?.()
    }
  }, [open])

  // Mover foco al primer elemento enfocable del modal al abrir.
  useEffect(() => {
    if (!open || !containerRef.current) return
    const focusable = containerRef.current.querySelectorAll<HTMLElement>(
      'input, select, textarea, button, a[href], [tabindex]:not([tabindex="-1"])',
    )
    // Saltar el botón "Cerrar" para enfocar el primer campo de contenido.
    const closeButton = containerRef.current.querySelector<HTMLElement>('[data-modal-close]')
    const first = Array.from(focusable).find((el) => el !== closeButton)
    first?.focus()
  }, [open])

  // Cierre por Escape + focus trap con Tab.
  useEffect(() => {
    if (!open) return
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && dismissible) {
        e.preventDefault()
        onClose()
        return
      }
      if (e.key === 'Tab' && containerRef.current) {
        const focusable = Array.from(
          containerRef.current.querySelectorAll<HTMLElement>(
            'input, select, textarea, button, a[href], [tabindex]:not([tabindex="-1"])',
          ),
        ).filter((el) => !el.hasAttribute('disabled') && el.offsetParent !== null)

        if (focusable.length === 0) return
        const first = focusable[0]
        const last = focusable[focusable.length - 1]
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault()
          last.focus()
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault()
          first.focus()
        }
      }
    }
    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [open, dismissible, onClose])

  if (!open) return null

  const handleOverlayMouseDown = (e: React.MouseEvent) => {
    mouseDownOnOverlay.current = e.target === e.currentTarget
  }
  const handleOverlayMouseUp = (e: React.MouseEvent) => {
    if (mouseDownOnOverlay.current && e.target === e.currentTarget && dismissible) {
      onClose()
    }
    mouseDownOnOverlay.current = false
  }

  return createPortal(
    <div
      className={`modal-overlay${size === 'fullscreen' ? ' modal-overlay--fullscreen' : ''}`}
      onMouseDown={handleOverlayMouseDown}
      onMouseUp={handleOverlayMouseUp}
    >
      <div
        ref={containerRef}
        className={`modal modal--${size}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? 'modal-title' : undefined}
        aria-describedby={description ? 'modal-desc' : undefined}
        aria-label={!title ? ariaLabel : undefined}
      >
        <header className="modal__header">
          {title && (
            <h2 id="modal-title" className="modal__title">
              {title}
            </h2>
          )}
          <button
            type="button"
            className="modal__close"
            onClick={onClose}
            data-modal-close
            aria-label="Cerrar"
          >
            ✕
          </button>
        </header>

        {description && (
          <p id="modal-desc" className="modal__desc">
            {description}
          </p>
        )}

        <div className="modal__body">{children}</div>

        {footer && <footer className="modal__footer">{footer}</footer>}
      </div>
    </div>,
    document.body,
  )
}
